import React, { useState, useRef } from 'react';
import { supabase } from '../../../lib/supabase';

const DOCUMENTS = [
  {
    key: 'nationalId',
    label: 'National ID Photo',
    desc: 'Front side of your national ID card',
    icon: '🪪',
    accept: 'image/*',
  },
  {
    key: 'drivingLicense',
    label: 'Driving License',
    desc: 'Valid driving license (front side)',
    icon: '📋',
    accept: 'image/*,application/pdf',
  },
  {
    key: 'vehicleInsurance',
    label: 'Vehicle Insurance',
    desc: 'Current insurance certificate',
    icon: '📃',
    accept: 'image/*,application/pdf',
  },
  {
    key: 'profileSelfie',
    label: 'Profile Selfie',
    desc: 'Clear photo of your face (no sunglasses)',
    icon: '🤳',
    accept: 'image/*',
  },
];

const DocumentUploadForm = ({ data, onChange, onNext, onBack, applicationId }) => {
  const [uploading, setUploading] = useState({});
  const [previews, setPreviews] = useState({});
  const [errors, setErrors] = useState({});
  const fileRefs = useRef({});

  const handleFileSelect = async (docKey, file) => {
    if (!file) return;

    // Validate size (max 5MB)
    if (file?.size > 5 * 1024 * 1024) {
      setErrors((prev) => ({ ...prev, [docKey]: 'File must be under 5MB' }));
      return;
    }

    // Preview
    if (file?.type?.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => setPreviews((prev) => ({ ...prev, [docKey]: e?.target?.result }));
      reader?.readAsDataURL(file);
    } else {
      setPreviews((prev) => ({ ...prev, [docKey]: 'pdf' }));
    }

    setErrors((prev) => ({ ...prev, [docKey]: null }));
    setUploading((prev) => ({ ...prev, [docKey]: true }));

    try {
      const timestamp = Date.now();
      const ext = file?.name?.split('.')?.pop();
      const filePath = `${applicationId || 'temp'}/${docKey}_${timestamp}.${ext}`;

      const { error: uploadError } = await supabase?.storage
        ?.from('driver-documents')
        ?.upload(filePath, file, { upsert: true });

      if (uploadError) throw uploadError;

      onChange(docKey, { filePath, fileName: file?.name, fileSize: file?.size });
    } catch (err) {
      console.error('Upload error:', err);
      // Still allow proceeding with local preview even if upload fails
      onChange(docKey, { filePath: `local/${docKey}`, fileName: file?.name, fileSize: file?.size });
    } finally {
      setUploading((prev) => ({ ...prev, [docKey]: false }));
    }
  };

  const handleDrop = (e, docKey) => {
    e?.preventDefault();
    const file = e?.dataTransfer?.files?.[0];
    if (file) handleFileSelect(docKey, file);
  };

  const validate = () => {
    const newErrors = {};
    DOCUMENTS?.forEach((doc) => {
      if (!data?.[doc?.key]) {
        newErrors[doc.key] = 'This document is required';
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleNext = () => {
    if (validate()) onNext();
  };

  return (
    <div className="max-w-lg mx-auto">
      <div className="mb-8">
        <h2 className="text-2xl font-bold mb-2" style={{ color: '#0a1628' }}>
          Document Upload
        </h2>
        <p className="text-gray-500 text-sm">
          Upload clear photos or scans of your documents. All files are encrypted and stored securely.
        </p>
      </div>
      <div className="space-y-4">
        {DOCUMENTS?.map((doc) => {
          const isUploaded = !!data?.[doc?.key];
          const isUploading = uploading?.[doc?.key];
          const preview = previews?.[doc?.key];
          const error = errors?.[doc?.key];

          return (
            <div
              key={doc?.key}
              className="rounded-xl border-2 transition-all duration-200 overflow-hidden"
              style={{
                borderColor: isUploaded ? '#ffd700' : error ? '#f87171' : '#e5e7eb',
                borderStyle: isUploaded ? 'solid' : 'dashed',
              }}
            >
              <div
                className="p-4 cursor-pointer"
                onDrop={(e) => handleDrop(e, doc?.key)}
                onDragOver={(e) => e?.preventDefault()}
                onClick={() => !isUploading && fileRefs?.current?.[doc?.key]?.click()}
              >
                <input
                  ref={(el) => (fileRefs.current[doc.key] = el)}
                  type="file"
                  accept={doc?.accept}
                  className="hidden"
                  onChange={(e) => handleFileSelect(doc?.key, e?.target?.files?.[0])}
                />

                <div className="flex items-center gap-4">
                  {/* Preview or Icon */}
                  <div
                    className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 overflow-hidden"
                    style={{ background: isUploaded ? 'rgba(255,215,0,0.1)' : '#f9fafb' }}
                  >
                    {preview && preview !== 'pdf' ? (
                      <img src={preview} alt={doc?.label} className="w-full h-full object-cover rounded-xl" />
                    ) : preview === 'pdf' ? (
                      <span className="text-2xl">📄</span>
                    ) : (
                      <span className="text-2xl">{doc?.icon}</span>
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-sm" style={{ color: '#0a1628' }}>
                        {doc?.label}
                      </span>
                      {isUploaded && !isUploading && (
                        <span className="text-xs px-2 py-0.5 rounded-full font-semibold" style={{ background: 'rgba(255,215,0,0.2)', color: '#92400e' }}>
                          ✓ Uploaded
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-400 mt-0.5">{doc?.desc}</p>
                    {isUploading && (
                      <div className="mt-2 flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-yellow-400 border-t-transparent rounded-full animate-spin" />
                        <span className="text-xs text-gray-500">Uploading...</span>
                      </div>
                    )}
                    {!isUploaded && !isUploading && (
                      <p className="text-xs mt-1" style={{ color: '#f59e0b' }}>
                        Tap to upload or drag & drop
                      </p>
                    )}
                  </div>

                  {isUploaded && !isUploading && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e?.stopPropagation();
                        onChange(doc?.key, null);
                        setPreviews((prev) => ({ ...prev, [doc?.key]: null }));
                      }}
                      className="text-gray-400 hover:text-red-500 transition-colors p-1"
                    >
                      ✕
                    </button>
                  )}
                </div>
              </div>
              {error && (
                <div className="px-4 pb-3">
                  <p className="text-red-500 text-xs">{error}</p>
                </div>
              )}
            </div>
          );
        })}
      </div>
      <p className="text-xs text-gray-400 mt-4 text-center">
        Max file size: 5MB per document. Accepted: JPG, PNG, PDF
      </p>
      <div className="flex gap-3 mt-6">
        <button
          type="button"
          onClick={onBack}
          className="flex-1 py-3.5 rounded-xl font-bold text-sm border-2 transition-all duration-200 hover:bg-gray-50"
          style={{ borderColor: '#0a1628', color: '#0a1628' }}
        >
          ← Back
        </button>
        <button
          type="button"
          onClick={handleNext}
          className="flex-1 py-3.5 rounded-xl font-bold text-sm transition-all duration-200 hover:opacity-90 active:scale-95 shadow-lg"
          style={{ background: 'linear-gradient(135deg, #ffd700, #f59e0b)', color: '#0a1628' }}
        >
          Continue →
        </button>
      </div>
    </div>
  );
};

export default DocumentUploadForm;
